import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  changeUnit,
  getAdjusterLookup,
  getClientLookup,
  getMaxSpeedLookup,
  getVehicleById,
  getVehicleMakeLookup,
  getVehicleModelLookup,
  getVehicleTypeLookup,
  installUnit,
  searchUnitByImeiOrSerial,
  searchVehicle,
  uninstallFuelSensor,
  uninstallUnit,
  type ChangeUnitPayload,
  type InstallUnitPayload,
  type UninstallUnitPayload,
  type UnitSearchParams,
  type VehicleDetailResponse,
  type VehicleSearchParams,
} from "src/services/unitManagementService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

type QueryOptions = { enabled?: boolean } & Record<string, unknown>;

const LOOKUP_STALE_TIME = 60 * 60 * 1000;

const useUnitManagementLookupQuery = (
  queryName: readonly string[],
  queryFn: (
    params: Record<string, unknown>,
    language?: string,
  ) => Promise<unknown>,
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...queryName, params, language],
      queryFn: () => queryFn(params, language),
      enabled,
      refetchOnMount: true,
      staleTime: LOOKUP_STALE_TIME,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useQueryGetUnitManagementVehicleMakeLookup = (
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) =>
  useUnitManagementLookupQuery(
    QueryNames.unitManagementVehicleMakeLookup,
    getVehicleMakeLookup,
    params,
    options,
  );

export const useQueryGetUnitManagementVehicleModelLookup = (
  makeId?: string | number,
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!makeId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [
        ...QueryNames.unitManagementVehicleModelLookup,
        makeId,
        params,
        language,
      ],
      queryFn: () =>
        getVehicleModelLookup(makeId as string | number, params, language),
      enabled,
      refetchOnMount: true,
      staleTime: LOOKUP_STALE_TIME,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useQueryGetUnitManagementVehicleTypeLookup = (
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) =>
  useUnitManagementLookupQuery(
    QueryNames.unitManagementVehicleTypeLookup,
    getVehicleTypeLookup,
    params,
    options,
  );

export const useQueryGetUnitManagementMaxSpeedLookup = (
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) =>
  useUnitManagementLookupQuery(
    QueryNames.unitManagementMaxSpeedLookup,
    getMaxSpeedLookup,
    params,
    options,
  );

export const useQueryGetUnitManagementAdjusterLookup = (
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) =>
  useUnitManagementLookupQuery(
    QueryNames.unitManagementAdjusterLookup,
    getAdjusterLookup,
    params,
    options,
  );

export const useQueryGetUnitManagementClientLookup = (
  params: Record<string, unknown> = {},
  options: QueryOptions = {},
) =>
  useUnitManagementLookupQuery(
    QueryNames.unitManagementClientLookup,
    getClientLookup,
    params,
    options,
  );

export const useQuerySearchUnitByImeiOrSerial = (
  params?: UnitSearchParams,
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled =
    !!params?.query &&
    params.query.trim().length >= 3 &&
    !!params.unitType &&
    !!params.distributorId &&
    options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.unitManagementUnitSearch, params, language],
      queryFn: () => searchUnitByImeiOrSerial(params as UnitSearchParams, language),
      enabled,
      refetchOnMount: false,
      retry: false,
      staleTime: 0,
      gcTime: 0,
      ...options,
    }),
    enabled,
  );
};

export const useMutationInstallUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: InstallUnitPayload) => installUnit(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleList });
      queryClient.invalidateQueries({
        queryKey: QueryNames.unitManagementUnitSearch,
      });
    },
  });
};

export const useQuerySearchVehicle = (
  params?: VehicleSearchParams,
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled =
    !!params?.clientId &&
    !!params?.query &&
    params.query.trim().length >= 3 &&
    options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.unitManagementVehicleSearch, params, language],
      queryFn: () => searchVehicle(params as VehicleSearchParams, language),
      enabled,
      refetchOnMount: false,
      retry: false,
      staleTime: 0,
      gcTime: 0,
      ...options,
    }),
    enabled,
  );
};

export const useQueryGetVehicleById = (
  vehicleId?: string | number,
  options: QueryOptions = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!vehicleId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [
        ...QueryNames.unitManagementVehicleDetail,
        vehicleId,
        language,
      ],
      queryFn: () => getVehicleById(vehicleId as string | number, language),
      enabled,
      select: (data): VehicleDetailResponse | undefined => data?.data?.response,
      refetchOnMount: true,
      retry: false,
      staleTime: 0,
      ...options,
    }),
    enabled,
  );
};

const invalidateUnitManagementQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: QueryNames.vehicleList });
  queryClient.invalidateQueries({
    queryKey: QueryNames.unitManagementUnitSearch,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.unitManagementVehicleSearch,
  });
  queryClient.invalidateQueries({
    queryKey: QueryNames.unitManagementVehicleDetail,
  });
};

export const useMutationUninstallUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UninstallUnitPayload) =>
      uninstallUnit(payload, language),
    onSuccess: () => invalidateUnitManagementQueries(queryClient),
  });
};

export const useMutationUninstallFuelSensor = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (fuelSensorAssociationId: string | number) =>
      uninstallFuelSensor(fuelSensorAssociationId, language),
    onSuccess: () => invalidateUnitManagementQueries(queryClient),
  });
};

export const useMutationChangeUnit = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: ChangeUnitPayload) => changeUnit(payload, language),
    onSuccess: () => invalidateUnitManagementQueries(queryClient),
  });
};
